// src/contexts/CompanyContext.js
// 代理商公司切換 Context — 管理 agency 使用者目前選取的客戶公司

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const CompanyContext = createContext();

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    // 在 CompanyProvider 外使用時（如登入頁），回傳安全預設值
    return {
      selectedCompanyId: null,
      selectedCompanyName: '',
      setSelectedCompanyId: () => {},
      getEffectiveCompanyId: () => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return String(user.companyId || user.company_id || '');
      },
      isAgencyUser: () => false,
      isViewingOwnCompany: () => true,
      clientList: [],
      setClientList: () => {}
    };
  }
  return context;
};

export const CompanyProvider = ({ children }) => {
  const getUser = () => JSON.parse(localStorage.getItem('user') || '{}');
  const getOwnCompanyId = () => {
    const user = getUser();
    return String(user.companyId || user.company_id || '');
  };

  // 初始化：從 localStorage 讀取上次選取的公司
  const [selectedCompanyId, setSelectedCompanyIdState] = useState(() => {
    const user = getUser();
    const isAgency = user.role === 'agency_admin' || user.role === 'agency_staff';
    if (!isAgency) return null;

    const saved = localStorage.getItem('agencySelectedCompanyId');
    return saved || getOwnCompanyId();
  });

  const [selectedCompanyName, setSelectedCompanyName] = useState(() => {
    return localStorage.getItem('agencySelectedCompanyName') || '';
  });

  const [clientList, setClientList] = useState([]);

  // 設定選取的公司（同時寫入 localStorage）
  const setSelectedCompanyId = useCallback((id, name) => {
    const strId = String(id);
    setSelectedCompanyIdState(strId);
    setSelectedCompanyName(name || '');
    localStorage.setItem('agencySelectedCompanyId', strId);
    localStorage.setItem('agencySelectedCompanyName', name || '');
  }, []);

  // 取得有效的 companyId：agency 使用者回傳選取的公司，一般使用者回傳自己的
  const getEffectiveCompanyId = useCallback(() => {
    const user = getUser();
    const isAgency = user.role === 'agency_admin' || user.role === 'agency_staff';
    if (isAgency && selectedCompanyId) {
      return String(selectedCompanyId);
    }
    return getOwnCompanyId();
  }, [selectedCompanyId]);

  const isAgencyUser = useCallback(() => {
    const user = getUser();
    return user.role === 'agency_admin' || user.role === 'agency_staff';
  }, []);

  // 是否正在檢視自己的公司
  const isViewingOwnCompany = useCallback(() => {
    return getEffectiveCompanyId() === getOwnCompanyId();
  }, [getEffectiveCompanyId]);

  const value = {
    selectedCompanyId,
    selectedCompanyName,
    setSelectedCompanyId,
    getEffectiveCompanyId,
    isAgencyUser,
    isViewingOwnCompany,
    clientList,
    setClientList
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};
